<template>
  <div id="pageCesion" @click="handleClickOutside">
    <v-container fluid>
      <!-- Estadísticas básicas -->
      <v-row class="mb-3" no-gutters justify="center">
        <!-- Totales -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="primary" size="28" class="mb-1">mdi-car-arrow-right</v-icon>
              <div class="text-h6 font-weight-bold">{{ totalMovimientos }}</div>
              <div class="text-caption grey--text">Totales</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- No reportados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="red" size="28" class="mb-1">mdi-alert-circle-outline</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementUnreported }}</div>
              <div class="text-caption grey--text">No reportados</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Reportados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="orange" size="28" class="mb-1">mdi-bell-ring-outline</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementReported }}</div>
              <div class="text-caption grey--text">Reportados</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- En proceso -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="purple" size="28" class="mb-1">mdi-progress-clock</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementProcessing }}</div>
              <div class="text-caption grey--text">En proceso</div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Completados -->
        <v-col class="d-flex">
          <v-card class="stat-card flex-grow-1 ma-1">
            <v-card-text class="pa-2 text-center">
              <v-icon color="green" size="28" class="mb-1">mdi-check-circle-outline</v-icon>
              <div class="text-h6 font-weight-bold">{{ movementCompleted }}</div>
              <div class="text-caption grey--text">Completados</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vista principal -->
      <v-row>
        <v-col cols="12">
          <v-card class="movement-card elevation-2">
            <!-- Header mejorado -->
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-car-arrow-right</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">
                Lista de cesiones
              </v-toolbar-title>
              <v-spacer></v-spacer>

              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field v-model="filter.search" label="Buscar cesión..." prepend-inner-icon="mdi-magnify"
                  hide-details clearable rounded single-line filled dense dark @input="applyFilter"></v-text-field>
              </v-col>

              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>

              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field ref="searchInput" v-model="filter.search" label="Buscar cesión..."
                  prepend-inner-icon="mdi-magnify" hide-details clearable rounded single-line filled dense dark
                  class="search-input-mobile" @click.stop @input="applyFilter"
                  @keydown.esc="showSearch = false"></v-text-field>
              </v-col>

              <!-- Menú de filtros -->
              <filter-menu title="Filtrar cesiones" :current-filter="currentFilter" @canceled="rollbackFilter()"
                @filtered="commitFilter()">
                <v-row>
                  <v-col>
                    <v-text-field v-model="filter['icc_number']" label="Número IIC" clearable outlined
                      hide-details="auto" class="mb-4"
                      prepend-inner-icon="mdi-card-account-details-outline"></v-text-field>

                    <v-text-field v-model="filter['brand']" label="Marca" clearable outlined hide-details="auto"
                      class="mb-4" prepend-inner-icon="mdi-tag-outline"></v-text-field>

                    <v-select v-model="filter['source_location_id']" :items="LOCATIONS" item-text="name" item-value="id"
                      label="Origen" clearable outlined hide-details="auto" class="mb-4"
                      prepend-inner-icon="mdi-map-marker-outline"></v-select>

                    <v-text-field v-model="filter['model']" label="Modelo" clearable outlined
                      hide-details="auto" prepend-inner-icon="mdi-car-info"></v-text-field>

                     <v-select 
                      v-if="$store.getters.HAS_ROLE(['colaborator']) || hasDealersAsociados || hasPartnerDealers" 
                      v-model="filter.dealer_id" 
                      :items="accessibleDealers" 
                      :loading="loadingAccessibleDealers"
                      item-value="id" 
                      item-text="name" 
                      label="Dealer" 
                      clearable 
                      outlined 
                      hide-details="auto" 
                      prepend-inner-icon="mdi-store"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="item.is_own ? 'primary' : 'grey'">
                          {{ item.is_own ? 'mdi-home' : 'mdi-store' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar size="24" class="mr-2">
                          <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo dealer">
                          <v-icon v-else :color="item.is_own ? 'primary' : 'grey'">
                            {{ item.is_own ? 'mdi-home' : 'mdi-store' }}
                          </v-icon>
                        </v-list-item-avatar>
                        {{ item.name }}
                        <v-chip v-if="item.is_own" x-small class="ml-2" color="primary">Propio</v-chip>
                      </template>
                    </v-select>
                  </v-col>

                  <v-col>
                    <v-text-field v-model="filter['plate']" label="Matrícula" clearable outlined
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-card-bulleted-outline"></v-text-field>

                    <v-text-field v-model="filter['frame_number']" label="Bastidor" clearable outlined
                      hide-details="auto" class="mb-4" prepend-inner-icon="mdi-identifier"></v-text-field>

                    <v-select v-model="filter['destination_location_id']" :items="LOCATIONS" item-text="name"
                      item-value="id" label="Destino" clearable outlined hide-details="auto" class="mb-4"
                      prepend-inner-icon="mdi-map-marker-radius"></v-select>

                    <v-select v-model="filter['status']" :items="estados" item-text="name" item-value="value"
                      label="Estado" clearable outlined hide-details="auto"
                      prepend-inner-icon="mdi-flag-variant-outline">
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="getStatusColor(item.value)">
                          {{ getStatusIcon(item.value) }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="getStatusColor(item.value)">
                          {{ getStatusIcon(item.value) }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                    </v-select>
                    
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botón agregar movimiento -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <div v-if="HAS_PERMISSION('crear_cesion')">
                    <v-btn icon v-on="on" @click="showMovimientoAdd()" class="mr-2" color="white">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </template>
                <span>Agregar Cesión</span>
              </v-tooltip>

              <!-- Botón actualizar -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="refreshDataTable" v-on="on" class="mr-2" color="white" :disabled="loadingItems">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>
            </v-toolbar>
            <cesion-form v-if="visibleForm" v-model="editItem" @save="updateDataTable" @cancel="visibleForm = false"></cesion-form>

            <!-- Filtros rápidos bajo la barra de herramientas -->
           <v-card-text class="py-2 px-4 mt-2">
              <!-- Filtros por condición y ubicación -->
              <v-row class="mb-0 align-left">
                <v-col cols="auto" v-if="hasVehicleConditionPermissions">
                  <div class="d-flex py-0">
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vn') && HAS_PERMISSION('vn')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('new')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('new') ? 'green' : 'green'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('new')"
                    >
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('vo') && HAS_PERMISSION('vo')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('used')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('used') ? 'amber' : 'amber'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('used')"
                    >
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>
                    <v-chip 
                      v-if="DEALER_HAS_TYPE('taller') && HAS_PERMISSION('taller')"
                      :outlined="!filter.vehicle_condition || !filter.vehicle_condition.includes('taller')"
                      :color="filter.vehicle_condition && filter.vehicle_condition.includes('taller') ? 'indigo' : 'indigo'"
                      small 
                      class="mr-1"
                      @click="applyConditionFilter('taller')"
                    >
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>
                  </div>
                </v-col>
                
                <!-- Filtros de ubicación (Origen y Destino) -->
                <v-col cols="auto">
                  <div class="d-flex py-0">
                    <!-- Filtro de ubicación de origen -->
                    <v-menu offset-y :close-on-content-click="false" max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          small
                          :color="filter['source_location_id'] && filter['source_location_id'].length > 0 ? 'primary' : 'primary'"
                          :outlined="!(filter['source_location_id'] && filter['source_location_id'].length > 0)"
                          class="mr-1 location-filter-chip"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon left x-small :color="filter['source_location_id'] && filter['source_location_id'].length > 0 ? 'white' : 'primary'">
                            mdi-map-marker
                          </v-icon>
                          <span v-if="filter['source_location_id'] && filter['source_location_id'].length > 0">
                            Origen ({{ filter['source_location_id'].length }})
                          </span>
                          <span v-else>Origen</span>
                          <v-icon 
                            v-if="filter['source_location_id'] && filter['source_location_id'].length > 0" 
                            right 
                            x-small 
                            class="ml-1"
                            @click.stop="clearSourceLocation"
                          >
                            mdi-close-circle
                          </v-icon>
                        </v-chip>
                      </template>
                      <v-card>
                        <v-card-title class="py-2 px-3 subtitle-2">
                          <v-icon small color="black" class="mr-2">mdi-map-marker</v-icon>
                          Ubicación de Origen
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-list dense max-height="300" class="overflow-y-auto">
                          <v-list-item
                            v-for="location in locationItems"
                            :key="location.id"
                            @click="toggleSourceLocation(location.id)"
                            :class="{ 'primary--text': filter['source_location_id'] && filter['source_location_id'].includes(location.id) }"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon small :color="filter['source_location_id'] && filter['source_location_id'].includes(location.id) ? 'primary' : 'grey'">
                                {{ filter['source_location_id'] && filter['source_location_id'].includes(location.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ location.displayName  }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card>
                    </v-menu>

                    <!-- Filtro de ubicación de destino -->
                    <v-menu offset-y :close-on-content-click="false" max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          small
                          :color="filter['destination_location_id'] && filter['destination_location_id'].length > 0 ? 'success' : 'success'"
                          :outlined="!(filter['destination_location_id'] && filter['destination_location_id'].length > 0)"
                          class="mr-1 location-filter-chip"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon left x-small :color="filter['destination_location_id'] && filter['destination_location_id'].length > 0 ? 'white' : 'success'">
                            mdi-map-marker-check
                          </v-icon>
                          <span v-if="filter['destination_location_id'] && filter['destination_location_id'].length > 0">
                            Destino ({{ filter['destination_location_id'].length }})
                          </span>
                          <span v-else>Destino</span>
                          <v-icon 
                            v-if="filter['destination_location_id'] && filter['destination_location_id'].length > 0" 
                            right 
                            x-small 
                            class="ml-1"
                            @click.stop="clearDestinationLocation"
                          >
                            mdi-close-circle
                          </v-icon>
                        </v-chip>
                      </template>
                      <v-card>
                        <v-card-title class="py-2 px-3 subtitle-2">
                          <v-icon small color="success" class="mr-2">mdi-map-marker-check</v-icon>
                          Ubicación de Destino
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-list dense max-height="300" class="overflow-y-auto">
                          <v-list-item
                            v-for="location in locationItems"
                            :key="location.id"
                            @click="toggleDestinationLocation(location.id)"
                            :class="{ 'success--text': filter['destination_location_id'] && filter['destination_location_id'].includes(location.id) }"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon small :color="filter['destination_location_id'] && filter['destination_location_id'].includes(location.id) ? 'success' : 'grey'">
                                {{ filter['destination_location_id'] && filter['destination_location_id'].includes(location.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{ location.displayName  }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>
              <!-- Filtros por estado -->
              <v-row class="align-center justify-center mt-0">
                <v-col cols="auto">
                  <v-chip-group v-model="quickFilter" @change="applyQuickFilter" class="py-1">
                     <v-chip filter outlined color="red" small class="mr-1">
                      <v-icon left x-small>mdi-alert-circle-outline</v-icon>
                      No reportados
                    </v-chip>
                    <v-chip filter outlined color="orange" small class="mr-1">
                      <v-icon left x-small>mdi-bell-ring-outline</v-icon>
                      Reportados
                    </v-chip>
                    <v-chip filter outlined color="purple" small class="mr-1">
                      <v-icon left x-small>mdi-progress-clock</v-icon>
                      En proceso
                    </v-chip>
                    <v-chip filter outlined color="green" small class="mr-1">
                      <v-icon left x-small>mdi-check-circle-outline</v-icon>
                      Completados
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col cols="auto" v-if="hasActiveFilters">
                  <v-btn x-small text color="primary" @click="resetFilters" class="ml-2">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Limpiar filtros
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-auto">
                  <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
                    <v-btn value="cards" small>
                      <v-icon small>mdi-view-grid-outline</v-icon>
                      Tarjetas
                    </v-btn>
                    <v-btn value="table" small>
                      <v-icon small>mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>

                    <!-- Filtro de Condición -->
                    <v-chip v-if="filter.vehicle_condition && filter.vehicle_condition.includes('new')" x-small class="mr-1 mb-1" close
                      color="green" text-color="white" @click:close="applyConditionFilter('new')">
                      <v-icon left x-small>mdi-car-limousine</v-icon>
                      VN
                    </v-chip>

                    <v-chip v-if="filter.vehicle_condition && filter.vehicle_condition.includes('used')" x-small class="mr-1 mb-1" close
                      color="amber" text-color="white" @click:close="applyConditionFilter('used')">
                      <v-icon left x-small>mdi-car-estate</v-icon>
                      VO
                    </v-chip>

                    <v-chip v-if="filter.vehicle_condition && filter.vehicle_condition.includes('taller')" x-small class="mr-1 mb-1" close
                      color="indigo" text-color="white" @click:close="applyConditionFilter('taller')">
                      <v-icon left x-small>mdi-tools</v-icon>
                      Taller
                    </v-chip>

                    <v-chip v-if="filter['icc_number']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['icc_number'] = null; commitFilter()">
                      IIC: {{ filter['icc_number'] }}
                    </v-chip>
                    <v-chip v-if="filter['brand']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['brand'] = null; commitFilter()">
                      Marca: {{ filter['brand'] }}
                    </v-chip>
                    <v-chip v-if="filter['model']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['model'] = null; commitFilter()">
                      Modelo: {{ filter['model'] }}
                    </v-chip>
                    <v-chip v-if="filter['plate']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['plate'] = null; commitFilter()">
                      Matrícula: {{ filter['plate'] }}
                    </v-chip>
                    <v-chip v-if="filter['frame_number']" x-small class="mr-1 mb-1" close color="blue-grey"
                      text-color="white" @click:close="filter['frame_number'] = null; commitFilter()">
                      Bastidor: {{ filter['frame_number'] }}
                    </v-chip>
                    
                    <!-- Filtros de origen (múltiples) -->
                    <template v-if="filter['source_location_id'] && filter['source_location_id'].length > 0">
                      <v-chip 
                        v-for="sourceId in filter['source_location_id']" 
                        :key="`source-${sourceId}`"
                        x-small 
                        class="mr-1 mb-1" 
                        close 
                        color="primary"
                        text-color="white" 
                        @click:close="toggleSourceLocation(sourceId)"
                      >
                        <v-icon left x-small>mdi-map-marker</v-icon>
                        Origen: {{ getLocationNameById(sourceId) }}
                      </v-chip>
                    </template>
                    
                    <!-- Filtros de destino (múltiples) -->
                    <template v-if="filter['destination_location_id'] && filter['destination_location_id'].length > 0">
                      <v-chip 
                        v-for="destId in filter['destination_location_id']" 
                        :key="`dest-${destId}`"
                        x-small 
                        class="mr-1 mb-1" 
                        close 
                        color="success"
                        text-color="white" 
                        @click:close="toggleDestinationLocation(destId)"
                      >
                        <v-icon left x-small>mdi-map-marker-check</v-icon>
                        Destino: {{ getLocationNameById(destId) }}
                      </v-chip>
                    </template>
                    
                    <v-chip v-if="filter['status']" x-small class="mr-1 mb-1" close
                      :color="getStatusColor(filter['status'])" text-color="white"
                      @click:close="filter['status'] = null; commitFilter()">
                      {{ translateStatus(filter['status']) }}
                    </v-chip>
                     <v-chip v-if="filter['dealer_id']" x-small class="mr-1 mb-1" close color="purple"
                      text-color="white" @click:close="filter['dealer_id'] = null; commitFilter()">
                      Dealer: {{ getDealerName(filter['dealer_id']) }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Estado de carga -->
            <div v-if="loadingItems" class="pa-3">
              <!-- Skeleton loaders para la vista de tabla -->
              <template v-if="viewMode === 'table'">
                <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
              </template>

              <!-- Skeleton loaders para vista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>

            <!-- Vista de tabla -->
            <v-card-text v-else-if="viewMode === 'table'" class="pa-0">
              <v-data-table :headers="headers" :items="MOVIMIENTOS" :options.sync="pagination"
                :server-items-length="totalItems" :loading="loadingItems" :footer-props="footer" :key="tableId"
                class="elevation-0" multi-sort>
                <!-- Columna de vehículo -->
                <template v-slot:item.vehicle="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar color="blue-grey lighten-4" size="36" class="mr-3">
                      <v-icon size="18" color="blue-grey darken-1">mdi-car</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.vehicle.brand }} {{ item.vehicle.model }}</div>
                      <div class="caption grey--text text--darken-1">
                        {{ item.vehicle.plate_number || 'Sin matrícula' }} | IIC: {{ item.vehicle.iic_number || 'N/A' }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Columna de ubicaciones -->
                <template v-slot:item.locations="{ item }">
                  <div class="d-flex align-center pa-2 rounded"
                       :class="{ 'orange lighten-5': item.additional_info && item.additional_info.trim() !== '' }">
                    <v-icon small color="blue-grey" class="mr-1">mdi-map-marker</v-icon>
                    <div class="grey--text text--darken-2">{{ item.sourceLocation.name }}</div>
                    <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                    <div>{{ item.destinationLocation.name }}</div>
                    <v-btn
                      v-if="item.additional_info && item.additional_info.trim() !== ''"
                      icon
                      x-small
                      color="orange darken-2"
                      class="ml-2"
                      @click="showAdditionalInfoDialog(item)"
                    >
                      <v-icon small>mdi-alert-circle-outline</v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- Columna de dealer, solo para colaboradores -->
                <template v-slot:item.dealer="{ item }">
                  <div v-if="item.vehicle && item.vehicle.dealer" class="d-flex align-center">
                    <v-avatar size="24" class="mr-2">
                      <img v-if="item.vehicle.dealer.imageUrl && item.vehicle.dealer.imageUrl.url" 
                           :src="item.vehicle.dealer.imageUrl.url" 
                           alt="Logo dealer">
                      <v-icon v-else size="16" :color="item.vehicle.dealer.is_colaborador ? 'primary' : 'grey'">
                        {{ item.vehicle.dealer.is_colaborador ? 'mdi-home' : 'mdi-store' }}
                      </v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.vehicle.dealer.name }}</div>
                      <div class="caption grey--text text--darken-1">{{ item.vehicle.dealer.city }}</div>
                    </div>
                  </div>
                  <div v-else class="grey--text">N/A</div>
                </template>

                <!-- Columna de fecha -->
                <template v-slot:item.datetime="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-calendar</v-icon>
                    {{ formatDate(getMovementDate(item)) }}
                  </div>
                </template>

                <!-- Columna de fecha de creación -->
                <template v-slot:item.created_at="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-clock-outline</v-icon>
                    {{ formatDateHours(item.created_at) }}
                  </div>
                </template>                <!-- Columna de fecha de fin -->
                <template v-slot:item.end_date="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-update</v-icon>
                    {{ item.status === 'completed' ? formatDateHours(item.updated_at) : formatCustomDate(item.end_date) }}
                  </div>
                </template>

                <!-- Columna de responsable -->
                <template v-slot:item.responsible="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-account-tie</v-icon>
                    {{ item.responsible.name + ' ' + item.responsible.lastname || 'No asignado' }}
                  </div>
                </template>

                <!-- Columna de estado -->
                <template v-slot:item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" small dark>
                    <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
                    {{ translateStatus(item.status) }}
                  </v-chip>
                </template>                <!-- Columna de acciones -->
                <template v-slot:item.action="{ item }">
                  <div class="d-inline-flex action-buttons">
                    <template>
                      <!-- Botón Editar -->
                      <v-tooltip top v-if="item.status !== 'completed' && canEditMovimiento(item)">
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="primary" @click="showMovimientoForm(item)" v-on="on">
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Editar</span>
                      </v-tooltip>

                      <!-- Botón Aceptar Movimiento -->
                      <v-tooltip top v-if="canAcceptMovimiento(item)">
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="success" @click="acceptMovimiento(item)" v-on="on">
                            <v-icon>mdi-check-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Aceptar cesión</span>
                      </v-tooltip>

                      <!-- Botón Eliminar -->
                      <v-tooltip top v-if="canDeleteMovimiento(item)" >
                        <template v-slot:activator="{ on }">
                          <v-btn icon small color="red" @click="deleteCesion(item)" v-on="on">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Eliminar</span>
                      </v-tooltip>
                    </template>

                    <!-- Botón Ver Detalles (siempre visible) -->
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="info"
                          :to="{ name: 'CesionDetail', params: { cesion_id: item.id } }" v-on="on">
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver detalles</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron cesiones</h3>
                    <p class="subtitle-1 grey--text mb-4">
                      Intenta con otros filtros o agrega una nueva cesión
                    </p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Vista de tarjetas -->
            <v-card-text v-else class="pa-3">
              <v-row>
                <v-col cols="12" sm="6" md="4" v-for="item in MOVIMIENTOS" :key="item.id">
                  <v-card outlined hover class="movement-item-card h-100">
                    <v-card-text class="pa-4">
                      <!-- Header de tarjeta con estado -->
                      <div class="d-flex justify-space-between align-center mb-3">
                        <v-chip :color="getStatusColor(item.status)" small dark>
                          <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
                          {{ translateStatus(item.status) }}
                        </v-chip>
                        <v-menu bottom left offset-y transition="slide-y-transition">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small v-bind="attrs" v-on="on">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item @click="showMovimientoForm(item)" v-if="item.status !== 'completed' && canEditMovimiento(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Editar</v-list-item-title>
                            </v-list-item>
                            <v-list-item :to="{ name: 'CesionDetail', params: { cesion_id: item.id } }">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="info">mdi-eye</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Ver detalles</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="acceptMovimiento(item)" v-if="canAcceptMovimiento(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="success">mdi-check-circle</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title class="success--text">Aceptar cesión</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>

                      <!-- Vehículo -->
                      <div class="d-flex mb-4">
                        <v-avatar size="56" color="blue-grey lighten-4" class="mr-3">
                          <v-icon size="32" color="blue-grey darken-1">mdi-car</v-icon>
                        </v-avatar>
                        <div>
                          <div class="subtitle-1 font-weight-medium mb-1">
                            {{ item.vehicle.brand }} {{ item.vehicle.model }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-card-bulleted-outline</v-icon>
                            {{ item.vehicle.plate_number || 'Sin matrícula' }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            <v-icon x-small class="mr-1">mdi-card-account-details-outline</v-icon>
                            IIC: {{ item.vehicle.iic_number || 'N/A' }}
                          </div>
                        </div>
                      </div>

                      <!-- Información básica -->
                      <v-list dense class="pa-0 transparent">
                        <!-- Origen y destino -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-map-marker-path</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle class="d-flex align-center pa-2 rounded"
                                                   :class="{ 'orange lighten-5': item.additional_info && item.additional_info.trim() !== '' }">
                              <span class="grey--text text--darken-2">{{ item.sourceLocation.name }}</span>
                              <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                              <span>{{ item.destinationLocation.name }}</span>
                              <v-btn
                                v-if="item.additional_info && item.additional_info.trim() !== ''"
                                icon
                                x-small
                                color="orange darken-2"
                                class="ml-2"
                                @click="showAdditionalInfoDialog(item)"
                              >
                                <v-icon small>mdi-alert-circle-outline</v-icon>
                              </v-btn>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha entrega -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-calendar</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ formatDate(getMovementDate(item)) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha de creación -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-clock-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ formatDateHours(item.created_at) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Fecha devolución -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-update</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.status === 'completed' ? formatDateHours(item.updated_at) : formatCustomDate(item.end_date) }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Responsable -->
                        <v-list-item class="px-0 py-1">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-account-tie</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.responsible.name + ' ' + item.responsible.lastname || 'No asignado' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <!-- Transporte -->
                        <v-list-item class="px-0 py-1" v-if="item.transportation">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="blue-grey darken-1">mdi-truck-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.transportation }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>

                      <!-- Acciones -->
                      <v-card-actions class="pa-0 mt-3">
                        <v-btn text small color="primary"
                          :to="{ name: 'CesionDetail', params: { cesion_id: item.id } }">
                          <v-icon left small>mdi-eye</v-icon>
                          Ver detalles
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn v-if="canAcceptMovimiento(item)" text small color="success"
                          @click="acceptMovimiento(item)">
                          <v-icon left small>mdi-check-circle</v-icon>
                          Aceptar
                        </v-btn>
                      </v-card-actions>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Sin resultados -->
              <v-row v-if="!MOVIMIENTOS.length && !loadingItems">
                <v-col cols="12" class="text-center pa-6">
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron cesiones</h3>
                  <p class="subtitle-1 grey--text mb-4">
                    Intenta con otros filtros o agrega un nuevo cesión
                  </p>
                </v-col>
              </v-row>

              <!-- Paginación -->
              <v-row v-if="MOVIMIENTOS.length > 0">
                <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                  <div class="text-center d-flex align-center mr-4">
                    <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                    <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                      hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                  </div>

                  <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                    :total-visible="7" circle @input="onPageChange"></v-pagination>

                  <span class="text-body-2 grey--text text--darken-1 ml-4">
                    {{ paginationText }}
                  </span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Diálogo de información adicional -->
    <v-dialog v-model="additionalInfoDialog" max-width="600">
      <v-card>
        <v-card-title class="headline orange lighten-4">
          <v-icon left color="orange darken-2">mdi-alert-circle-outline</v-icon>
          Información Adicional
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <div v-if="selectedMovement">
            <!-- Información del vehículo -->
            <div class="mb-3">
              <div class="subtitle-2 grey--text text--darken-1 mb-1">Vehículo:</div>
              <div class="body-1 font-weight-medium">
                {{ selectedMovement.vehicle.brand }} {{ selectedMovement.vehicle.model }}
              </div>
              <div class="caption grey--text">
                {{ selectedMovement.vehicle.plate_number || 'Sin matrícula' }} |
                IIC: {{ selectedMovement.vehicle.iic_number || 'N/A' }}
              </div>
            </div>

            <!-- Ubicaciones -->
            <div class="mb-3">
              <div class="subtitle-2 grey--text text--darken-1 mb-1">Ruta:</div>
              <div class="body-1">
                <v-icon small color="blue-grey" class="mr-1">mdi-map-marker</v-icon>
                {{ selectedMovement.sourceLocation?.name || 'N/A' }}
                <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                {{ selectedMovement.destinationLocation?.name || 'N/A' }}
              </div>
            </div>

            <!-- Información adicional -->
            <div>
              <div class="subtitle-2 grey--text text--darken-1 mb-2">Información:</div>
              <v-card outlined class="pa-3 orange lighten-5">
                <div class="body-1" style="white-space: pre-wrap; word-break: break-word;">
                  {{ selectedMovement.additional_info }}
                </div>
              </v-card>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="additionalInfoDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import moment from 'moment-timezone'
import FilterMenu from '../../components/FilterMenu'
import MovimientoService from '@/services/MovimientoService'
import DealerService from '../../services/DealerService'
import LocationService from '../../services/LocationService'
import errorHandler from '../../mixins/errorHandler'

export default {
  components: {
    FilterMenu,
    CesionForm: () => import(/* webpackPrefetch: true */ './CesionForm')
  },

  mixins: [errorHandler],

  data() {
    return {
      // Vista y carga
      manualViewMode: null,
      loadingItems: false,
      totalItems: 0,
      tableId: 0,
      showSearch: false,
      quickFilter: null,
      conditionFilter: null,
      movementUnreported: 0,
      totalMovimientos: 0,
      movementReported: 0,
      movementProcessing: 0,
      movementCompleted: 0,
      locationItems: [],
      loadingLocations: false,
      // datatable
      footer: {
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        itemsPerPageText: 'Cesiones por página:',
        pageText: '{0}-{1} de {2}'
      },
      // filtro
      filter: {
        search: null,
        'icc_number': null,
        'brand': null,
        'model': null,
        'plate': null,
        'frame_number': null,
        'source_location_id': [],
        'destination_location_id': [],
        'status': null,
        dealer_id: null,
        vehicle_condition: []
      },
      currentFilter: {
        search: null,
        'icc_number': null,
        'brand': null,
        'model': null,
        'plate': null,
        'frame_number': null,
        'source_location_id': [],
        'destination_location_id': [],
        'status': null,
        dealer_id: null,
        vehicle_condition: []
      },
      estados: [
        { name: 'No Reportado', value: 'unreported' },
        { name: 'Reportado', value: 'reported' },
        { name: 'Recepcionado', value: 'transport' },
        { name: 'En Proceso', value: 'processing' },
        { name: 'Completado', value: 'completed' }
      ],
      // form
      visibleForm: false,
      editItem: null,
      roles: [],
      accessibleDealers: [],
      loadingAccessibleDealers: false,
      // Diálogo de información adicional
      additionalInfoDialog: false,
      selectedMovement: null
    }
  },

  async created () {
    // Bandera para evitar recarga durante la inicialización
    this.isInitializing = true
    const stats = await MovimientoService.getStats()
    // Calcular totales sumando todos los tipos de movimientos
    const unreported = (stats.data.data.unreported.assignment || 0)

    const reported = (stats.data.data.reported.assignment || 0)

    const processing = (stats.data.data.processing.assignment || 0)

    const completed = (stats.data.data.completed.assignment || 0)
    
    this.totalMovimientos = unreported + reported + processing + completed
    this.movementUnreported = unreported
    this.movementReported = reported
    this.movementProcessing = processing
    this.movementCompleted = completed
    this.roles = this.CURRENT_USER_ROLES

    // Primero cargar ubicaciones
    this.$store.dispatch('LOAD_LOCATIONS').then(() => {
      // Cargar las ubicaciones personalizadas
      this.loadLocations()
      
      // Luego cargar los datos con paginación
      this.loadData()

      // Desactivar la bandera de inicialización después de la carga inicial
      this.$nextTick(() => {
        this.isInitializing = false
      })
    }).catch(error => {
      this.handleApiError(error, 'Error al cargar ubicaciones')
      this.isInitializing = false
    })

    await this.loadAccessibleDealers()
  },

  methods: {
    loadData () {
      this.loadingItems = true
      const filterWithTypes = {
        ...this.filter,
        types: 'assignment'
      }
      //  if (this.$store.getters.HAS_ROLE(['colaborator'])) {
      //   filterWithTypes.status = ['unreported', 'processing']
      // }
      this.filter.type = 'assignment'
      this.$store.dispatch('LOAD_MOVIMIENTOS', filterWithTypes).then(result => {
        this.loadingItems = false
        // Asegurarse de que la página actual es válida
        const maxPage = Math.ceil(result / this.pagination.itemsPerPage)
        if (this.pagination.page > maxPage && maxPage > 0) {
          this.pagination.page = maxPage
        }
        this.totalItems = result
      }).catch(error => {
        this.handleApiError(error, 'Error al cargar las cesiones')
        this.loadingItems = false
      })
    },
    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 500),
    
    async loadStats(filterParams = null) {
      try {
        // Si no se pasan parámetros de filtro, usar los filtros actuales
        const statsParams = filterParams || this.buildFilterParams()
        
        const stats = await MovimientoService.getStats(statsParams)
        
        // Calcular totales sumando solo las cesiones (assignment)
        const unreported = (stats.data.data.unreported?.assignment || 0)
        const reported = (stats.data.data.reported?.assignment || 0)
        const processing = (stats.data.data.processing?.assignment || 0)
        const completed = (stats.data.data.completed?.assignment || 0)
        
        this.totalMovimientos = unreported + reported + processing + completed
        this.movementUnreported = unreported
        this.movementReported = reported
        this.movementProcessing = processing
        this.movementCompleted = completed
        
        // Forzar actualización de la UI
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      } catch (error) {
        this.handleApiError(error, 'Error al cargar las estadísticas de cesiones')
      }
    },
    buildFilterParams() {
      const params = { ...this.filter }
      
      // Limpiar arrays vacíos
      if (params.vehicle_condition && params.vehicle_condition.length === 0) {
        delete params.vehicle_condition
      }
      if (params.dealer_id && params.dealer_id.length === 0) {
        delete params.dealer_id
      }
      if (params.status && params.status.length === 0) {
        delete params.status
      }
      
      // Para colaboradores, incluir solo los dealers accesibles si no hay filtro específico
      if (this.$store.getters.HAS_ROLE(['colaborator']) && !params.dealer_id) {
        if (this.accessibleDealers && this.accessibleDealers.length > 0) {
          params.dealer_id = this.accessibleDealers.map(d => d.id)
        }
      }
      
      return params
    },
    updateDataTable ({ insert }) {
      this.visibleForm = false
      if (insert) {
        this.totalItems++
      }
      this.refreshDataTable()
      this.$toast.success(insert ? 'Cesión creado con éxito' : 'Cesión actualizado con éxito')
    },
    refreshDataTable () {
      this.tableId++
      this.loadData()
      this.loadStats(this.buildFilterParams()) // Recargar estadísticas
    },
    // Manejo de búsqueda en móvil
    toggleSearch () {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    },
    handleClickOutside (event) {
      const searchInput = this.$refs.searchInput
      if (searchInput && !searchInput.$el.contains(event.target)) {
        this.showSearch = false
      }
    },

    // Filtros
    applyFilter () {
      this.debouncedLoadData()
      this.loadStats(this.buildFilterParams())
    },
    commitFilter () {
      for (const key in this.currentFilter) {
        this.currentFilter[key] = this.filter[key]
      }
      this.loadData()
      this.loadStats(this.buildFilterParams())
    },
    rollbackFilter () {
      for (const key in this.currentFilter) {
        this.filter[key] = this.currentFilter[key]
      }
    },
    resetFilters () {
      for (const key in this.filter) {
        if (key === 'vehicle_condition' || key === 'source_location_id' || key === 'destination_location_id') {
          this.filter[key] = []
        } else {
          this.filter[key] = null
        }
      }
      this.quickFilter = null
      this.conditionFilter = null
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },
    getMovementDate (item) {
      return item.delivery_date || item.datetime
    },
    applyQuickFilter (index) {
      if (index === null) {
        this.filter.status = null
      } else {
        const statusValues = ['unreported', 'reported', 'processing', 'completed']
        this.filter.status = statusValues[index]
      }
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },
    applyConditionFilter(condition) {
      // Si el array no está inicializado, inicializarlo
      if (!Array.isArray(this.filter.vehicle_condition)) {
        this.filter.vehicle_condition = []
      }
      
      // Si la condición ya está en el array, quitarla
      const index = this.filter.vehicle_condition.indexOf(condition)
      if (index > -1) {
        this.filter.vehicle_condition.splice(index, 1)
      } else {
        // Si no está, agregarla
        this.filter.vehicle_condition.push(condition)
      }
      
      this.commitFilter()
      this.loadStats(this.buildFilterParams())
    },
    // Recuperar nombre de ubicación
    getLocationName (locationId) {
      const location = this.LOCATIONS.find(l => l.id === locationId)
      return location ? location.name : 'Desconocido'
    },
    getLocationNameById (locationId) {
      const location = this.locationItems.find(l => l.id === locationId)
      return location ? location.displayName : 'Desconocido'
    },
    getDealerName (dealerId) {
      const dealer = this.accessibleDealers.find(d => d.id === dealerId)
      return dealer ? dealer.name : 'Desconocido'
    },

    async loadLocations () {
      this.loadingLocations = true
      try {
        const hasPartners = this.$store.getters.IS_PARTNER
        if (hasPartners) {
          const response = await LocationService.getPartnerLocations()
          const rawLocations = response.data?.data?.locations || []
          this.locationItems = rawLocations.map(loc => ({
            ...loc,
            displayName: loc.dealer_name
              ? `${loc.name} (${loc.dealer_name})`
              : loc.name
          }))
        } else {
          const response = await LocationService.get()
          let locations = []
          if (response.data && response.data.data && Array.isArray(response.data.data)) {
            locations = response.data.data
          }
          this.locationItems = locations.map(loc => ({
            ...loc,
            displayName: loc.dealer && loc.dealer.name
              ? `${loc.name} (${loc.dealer.name})`
              : loc.name
          }))
        }
      } catch (error) {
        this.$toast && this.$toast.error('Error al cargar ubicaciones')
      } finally {
        this.loadingLocations = false
      }
    },

    toggleSourceLocation (locationId) {
      if (!this.filter['source_location_id']) { this.filter['source_location_id'] = [] }
      const index = this.filter['source_location_id'].indexOf(locationId)
      if (index > -1) {
         this.filter['source_location_id'].splice(index, 1)
      } else { this.filter['source_location_id'].push(locationId) }
      this.commitFilter()
    },

    clearSourceLocation () {
      this.filter['source_location_id'] = []
      this.commitFilter()
    },

    toggleDestinationLocation (locationId) {
      if (!this.filter['destination_location_id']) { this.filter['destination_location_id'] = [] }
      const index = this.filter['destination_location_id'].indexOf(locationId)
      if (index > -1) { 
        this.filter['destination_location_id'].splice(index, 1) 
      } else { this.filter['destination_location_id'].push(locationId) }
      this.commitFilter()
    },

    clearDestinationLocation () {
      this.filter['destination_location_id'] = []
      this.commitFilter()
    },

    // Acciones
    showMovimientoForm (item) {
      this.editItem = item ? { ...item } : null
      this.visibleForm = true
    },
    showMovimientoAdd () {
      this.$router.push({ name: 'Escaneo' })
    },
    showAdditionalInfoDialog (item) {
      this.selectedMovement = item
      this.additionalInfoDialog = true
    },
    /**
     * El usuario puede aceptar (unreported → reported) si:
     * 1. El movimiento está en estado 'unreported'
     * 2. Si el vehículo es de un dealer partner: SOLO verifica permiso cruzado 'aceptar_cesion' en ese partner
     * 3. Si el vehículo es de su dealer propio: verifica permiso 'aceptar_cesion' local
     */
    canAcceptMovimiento (item) {
      // Validaciones básicas
      if (item.status !== 'unreported') return false
      if (!this.CURRENT_USER?.dealer) return false
      if (!item.vehicle?.dealer) return false

      const userDealerId = this.CURRENT_USER.dealer.id
      const vehicleDealerId = item.vehicle.dealer.id

      // Si el vehículo es de un dealer partner, SOLO verificar permiso cruzado (no verificar permiso local)
      if (vehicleDealerId !== userDealerId) {
        return this.$store.getters.HAS_PARTNER_PERMISSION({
          dealerId: vehicleDealerId,
          permission: 'aceptar_cesion'
        })
      }

      // Si el vehículo es del dealer propio, verificar permiso local
      return this.HAS_PERMISSION('aceptar_cesion')
    },

    /**
     * El usuario puede editar la cesión si:
     * 1. Si el vehículo es de un dealer partner: SOLO verifica permiso cruzado en ese partner
     * 2. Si el vehículo es de su dealer propio: verifica permiso local
     * 3. Colaboradores mantienen su lógica especial
     */
    canEditMovimiento (item) {
      if (item.status === 'completed') return false
      if (!item.vehicle?.dealer) return false

      const vehicleDealerId = item.vehicle.dealer.id
      const myDealerId = this.CURRENT_USER?.dealer?.id

      // Colaborador: solo cesiones de su propio dealer (lógica original)
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        if (!myDealerId) return false
        return item.status !== 'unreported' && vehicleDealerId === myDealerId
      }

      // Si el vehículo es de un dealer partner, SOLO verificar permiso cruzado (no verificar permiso local)
      if (vehicleDealerId !== myDealerId) {
        return this.$store.getters.HAS_PARTNER_PERMISSION({
          dealerId: vehicleDealerId,
          permission: 'editar_cesion'
        })
      }

      // Si el vehículo es del dealer propio, verificar permiso local
      return this.HAS_PERMISSION('editar_cesion')
    },

    /**
     * El usuario puede eliminar la cesión si:
     * 1. Si el vehículo es de un dealer partner: SOLO verifica permiso cruzado en ese partner
     * 2. Si el vehículo es de su dealer propio: verifica permiso local
     * 3. Colaboradores mantienen su lógica especial
     */
    canDeleteMovimiento (item) {
      if (!item.vehicle?.dealer) return false

      const vehicleDealerId = item.vehicle.dealer.id
      const myDealerId = this.CURRENT_USER?.dealer?.id

      // Colaborador: solo las de su propio dealer
      if (this.$store.getters.HAS_ROLE(['colaborator'])) {
        if (!myDealerId) return false
        return vehicleDealerId === myDealerId
      }

      // Si el vehículo es de un dealer partner, SOLO verificar permiso cruzado (no verificar permiso local)
      if (vehicleDealerId !== myDealerId) {
        return this.$store.getters.HAS_PARTNER_PERMISSION({
          dealerId: vehicleDealerId,
          permission: 'eliminar_cesion'
        })
      }

      // Si el vehículo es del dealer propio, verificar permiso local
      return this.HAS_PERMISSION('eliminar_cesion')
    },
    async acceptMovimiento (item) {
      try {
        this.$confirm(`¿Está seguro que desea cambiar el estado del movimiento a "${this.getNextStatusName(item.status)}"?`).then(async result => {
          if (result) {
            this.loadingItems = true

            await MovimientoService.aceptarMovimiento(item.id).then((response) => {
              this.loadingItems = false
              if (response.error) {
                this.handleApiError(response, 'Ocurrió un error al cambiar el estado de la cesión')
                return
              }
              this.$toast.success('Estado de la cesión actualizado con éxito')
              this.loadData()
              this.loadStats() // Recargar estadísticas
            }).catch(error => {
              this.handleApiError(error, 'Ocurrió un error al cambiar el estado de la cesión')
              this.loadingItems = false
            })
          }
        })
      } catch (error) {
        this.handleApiError(error, 'Ocurrió un error al cambiar el estado de la cesión')
        this.loadingItems = false
      }
    },
    getNextStatusName (currentStatus) {
      const statusOrder = ['unreported', 'reported', 'transport', 'processing', 'completed']
      let currentStatusIndex = statusOrder.indexOf(currentStatus)
      if (currentStatusIndex !== -1 && currentStatusIndex < statusOrder.length - 1) {
        let nextStatus = statusOrder[currentStatusIndex + 1]
        return this.translateStatus(nextStatus)
      }
      return this.translateStatus(currentStatus)
    },
    formatCustomDate (dateInput) {
      if (!dateInput) return 'N/A'
      const ts = Number(dateInput)
      if (Number.isNaN(ts)) return 'N/A'
      return moment.unix(ts).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },
    formatDateHours (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },
    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },
    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },
    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    },
    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },
    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },
    deleteCesion(item) {
      this.$confirm('¿Está seguro que desea eliminar esta cesión?').then(async (result) => {
        if (result) {
          try {
            await MovimientoService.delete(item.id)
            this.$toast.success('Cesión eliminada con éxito')
            this.loadData()
            this.loadStats() // Recargar estadísticas
          } catch (error) {
            this.handleApiError(error, 'Ocurrió un error al eliminar la cesión')
          }
        }
      })
    },
    async loadAccessibleDealers () {
      // Cargar para colaboradores Y para dealers con partners
      try {
        this.loadingAccessibleDealers = true
        const response = await DealerService.getMyAccessibleDealers()
        
        // Verificar la estructura de la respuesta
        let dealersArray = []
        if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
          dealersArray = response.data.data.data
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          dealersArray = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          dealersArray = response.data
        }
        
        this.accessibleDealers = dealersArray
      } catch (error) {
        this.handleApiError(error, 'Error al cargar la lista de dealers')
      } finally {
        this.loadingAccessibleDealers = false
      }
    }
  },

  computed: {
    ...mapGetters([
      'MOVIMIENTOS',
      'CURRENT_USER_ROLES',
      'LOCATIONS',
      'HAS_PERMISSION',
      'DEALER_HAS_TYPE',
      'CURRENT_USER',
      // Getters de partners
      'HAS_PARTNER_PERMISSION',
      'HAS_PERMISSION_ANYWHERE',
      'PARTNER_DEALER_IDS'
    ]),

    // ¿El dealer del usuario pertenece a algún grupo partner?
    hasPartnerDealers () {
      return this.$store.getters.IS_PARTNER || this.$store.getters.IN_SWITCHED_SESSION
    },

    hasDealersAsociados () {
      const user = this.$store.getters.CURRENT_USER
      if (!user || !user.dealer) return false
      return (
        (Array.isArray(user.dealers_asociados) && user.dealers_asociados.length > 0) ||
        !!user.dealer.is_colaborador
      )
    },

    headers() {
      let headers = [
        { text: 'Vehículo', align: 'start', value: 'vehicle', sortable: false },
        { text: 'Ubicaciones', value: 'locations', sortable: false }
      ]
      
      // Mostrar columna dealer si es colaborador O si tiene dealers asociados O si tiene dealers partner
      if (this.$store.getters.HAS_ROLE(['colaborator']) || this.hasDealersAsociados || this.hasPartnerDealers) {
        headers.push({ text: 'Dealer', value: 'dealer', sortable: false })
      }
      
      headers = headers.concat([
        { text: 'Fecha entrega', value: 'datetime', sortable: true },
        { text: 'Creación', value: 'created_at' },
        { text: 'Devolución', value: 'end_date', sortable: true },
        { text: 'Responsable', value: 'responsible', sortable: false },
        { text: 'Estado', value: 'status', align: 'center' },
        { text: 'Acciones', value: 'action', sortable: false, align: 'center', width: '120px' }
      ])
      return headers
    },
    pagination: {
      get: function () {
        return this.$store.getters.MOVIMIENTOS_PAG
      },
      set: function (value) {
        this.$store.commit('SET_MOVIMIENTOS_PAG', value)
      }
    },
    paginationText () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.totalItems)
      return `${start}-${end} de ${this.totalItems}`
    },
    searchTerm () {
      return this.filter.search
    },
    hasActiveFilters () {
      return Object.values(this.filter).some(value => {
        if (Array.isArray(value)) {
          return value.length > 0
        }
        return value !== null && value !== undefined && value !== ''
      })
    },
    hasVehicleConditionPermissions() {
      // Mostrar filtros si el usuario tiene al menos 1 tipo de vehículo activo en el dealer
      const types = ['vn', 'vo', 'taller']
      const count = types.reduce((acc, type) => {
        // Primero verificar si el dealer tiene el tipo, luego verificar permisos
        return acc + (this.DEALER_HAS_TYPE(type) && this.HAS_PERMISSION(type) ? 1 : 0)
      }, 0)
      return count >= 1
    },
    viewMode: {
      get() {
        if (this.manualViewMode) return this.manualViewMode
        return this.$vuetify.breakpoint.mdAndUp ? 'table' : 'cards'
      },
      set(value) {
        this.manualViewMode = value
      }
    }
  },

  watch: {
    pagination: {
      handler: _.debounce(function (newVal, oldVal) {
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        if (relevantPropsChanged && !this.loadingItems) {
          this.loadData()
        }
      }, 200), // pequeño delay
      deep: true
    },

    searchTerm () {
      this.debouncedLoadData()
    },
    viewMode (newVal, oldVal) {
    },
    manualViewMode() {
      this.tableId++
    }
  }
}
</script>

<style scoped>
.movement-card {
  border-radius: 8px;
  overflow: hidden;
}
.stat-card {
  border-radius: 8px;
  transition: all 0.3s;
  /* Force square cards - compact size */
  width: 110px;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
}

.stat-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-3px);
}

.stat-card .v-card__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px !important;
  box-sizing: border-box;
}

.stat-card .text-h6 {
  line-height: 1;
  font-size: 1.25rem !important;
  margin: 2px 0;
}

.stat-card .text-caption {
  line-height: 1.1;
  font-size: 0.85rem !important;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
}

.movement-item-card {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  border-radius: 8px;
}

.movement-item-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.action-buttons button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-buttons button:hover {
  opacity: 1;
}

/* Chip de filtro de ubicación */
.location-filter-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.location-filter-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Animación para nuevos elementos */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-item {
  animation: highlight 2s ease-in-out;
}

.search-input-mobile {
  max-width: 200px;
}
</style>
